import React, { useState } from 'react';
import { InquiryHero, SuccessBanner, PrivacyNote, FormActions, submitInquiry } from './formUtils';
import { Section, TextField, TextareaField, SelectField, MultiSelectField, FileUploadField, ConditionalText, ConsentSection } from './FormFields';
import { CMO_OPTIONS, HOW_HEARD_OPTIONS, COUNTRY_LIST } from '../../data/partnerOptions';

const initial = {
  fullName: '', company: '', jobTitle: '', email: '', phone: '',
  countryIncorporation: '', countrySubsidiaries: [],
  industrySector: '', industrySectorOther: '',
  productName: '', productStatus: '', puritySpec: '', puritySpecCustom: '', packaging: '',
  batchSize: '', annualDemand: '', orderFrequency: '', firstDelivery: '',
  qualityStandards: [], qualityStandardsOther: '', supplyChain: [], shippingDestination: '',
  pricingModel: '', additionalSpecs: '', howHeard: '',
  consentRequired: false, consentOptional: false,
};

export const CMOForm = () => {
  const [data, setData] = useState(initial);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showConsentError, setShowConsentError] = useState(false);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.consentRequired) { setShowConsentError(true); return; }
    setError('');
    setSubmitting(true);
    try {
      const sections = [
        { title: 'Contact Information', fields: [
          { label: 'Full name', value: data.fullName },
          { label: 'Company / organization', value: data.company },
          { label: 'Job title / role', value: data.jobTitle },
          { label: 'Business email', value: data.email },
          { label: 'Phone number', value: data.phone },
        ]},
        { title: 'Product Specifications', fields: [
          { label: 'Industry sector', value: data.industrySector },
          { label: 'Industry sector - specified', value: data.industrySectorOther },
          { label: 'Product name / CAS number', value: data.productName },
          { label: 'Product development status', value: data.productStatus },
          { label: 'Purity specification', value: data.puritySpec },
          { label: 'Custom purity specification', value: data.puritySpecCustom },
          { label: 'Packaging requirements', value: data.packaging },
        ]},
        { title: 'Volume & Demand', fields: [
          { label: 'Batch size (per order)', value: data.batchSize },
          { label: 'Estimated annual demand', value: data.annualDemand },
          { label: 'Order frequency', value: data.orderFrequency },
          { label: 'First delivery needed by', value: data.firstDelivery },
        ]},
        { title: 'Quality & Compliance', fields: [
          { label: 'Quality / regulatory requirements', value: data.qualityStandards },
          { label: 'Quality / regulatory - other specified', value: data.qualityStandardsOther },
          { label: 'Supply chain requirements', value: data.supplyChain },
          { label: 'Shipping destination', value: data.shippingDestination },
        ]},
        { title: 'Commercial Terms & Additional Details', fields: [
          { label: 'Preferred pricing model', value: data.pricingModel },
          { label: 'Additional specifications or comments', value: data.additionalSpecs },
        ]},
        { title: 'Referral', fields: [
          { label: 'How did you hear about LAMKO?', value: data.howHeard },
        ]},
        { title: 'Consent & Data Protection', fields: [
          { label: 'Required data-processing consent', value: data.consentRequired ? 'Yes - consent given' : 'No' },
          { label: 'Optional marketing consent', value: data.consentOptional ? 'Yes' : 'No' },
        ]},
      ];
      await submitInquiry({
        formType: 'cmo', sections,
        meta: { fullName: data.fullName, companyName: data.company, businessEmail: data.email, country: data.shippingDestination },
        files,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  const reset = () => { setData(initial); setFiles([]); setError(''); setSuccess(false); };

  return (
    <div className="min-h-screen bg-gray-50">
      <InquiryHero
        title="Contract Manufacturing (CMO)"
        subtitle="Commercial-scale production & supply chain"
      />
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {success && <SuccessBanner />}
            {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm" data-testid="error-banner">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-8">
              <Section title="Contact Information">
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="fullName" label="Full name" required helper="Your first and last name." placeholder="e.g., James Chen" value={data.fullName} onChange={set('fullName')} />
                  <TextField name="company" label="Company / organization" required helper="Legal entity or institution name." placeholder="e.g., ABC Co., Ltd." value={data.company} onChange={set('company')} />
                  <TextField name="jobTitle" label="Job title / role" required helper="Your current position." placeholder="e.g., VP of Supply Chain" value={data.jobTitle} onChange={set('jobTitle')} />
                  <TextField name="email" type="email" label="Business email" required helper="Primary email for order and contract correspondence." placeholder="e.g., jchen@company.com" value={data.email} onChange={set('email')} />
                  <TextField name="phone" type="tel" label="Phone number" helper="Include country code." placeholder="e.g., +82-10-1234-5678" value={data.phone} onChange={set('phone')} />
                </div>
                <SelectField name="countryIncorporation" label="Country of incorporation (Headquarters)" required helper="Where is your company legally registered?" options={COUNTRY_LIST} value={data.countryIncorporation} onChange={set('countryIncorporation')} />
              </Section>

              <Section title="Product Specifications">
                <SelectField name="industrySector" label="Industry sector" required helper="Target market for the manufactured product." options={CMO_OPTIONS.industrySector} value={data.industrySector} onChange={set('industrySector')} />
                <ConditionalText show={data.industrySector === 'Other (please specify)'} name="industrySectorOther" placeholder="Please describe your industry sector" value={data.industrySectorOther} onChange={set('industrySectorOther')} />
                <TextField name="productName" label="Product name / CAS number" required helper="Provide the exact product identifier, chemical name, or CAS number." placeholder="e.g., 2-TNATA / CAS 139255-16-6" value={data.productName} onChange={set('productName')} />
                <SelectField name="productStatus" label="Product development status" required helper="Has this product been manufactured before?" options={CMO_OPTIONS.productStatus} value={data.productStatus} onChange={set('productStatus')} />
                <SelectField name="puritySpec" label="Purity specification" required helper="Required purity of the final product." options={CMO_OPTIONS.puritySpec} value={data.puritySpec} onChange={set('puritySpec')} />
                <ConditionalText show={data.puritySpec === 'Custom specification'} name="puritySpecCustom" helper="Please describe your custom purity specification." placeholder="e.g., > 99.999% with specific metal limits" value={data.puritySpecCustom} onChange={set('puritySpecCustom')} />
                <SelectField name="packaging" label="Packaging requirements" helper="How should the final product be packaged?" options={CMO_OPTIONS.packaging} value={data.packaging} onChange={set('packaging')} />
              </Section>

              <Section title="Volume & Demand">
                <SelectField name="batchSize" label="Batch size (per order)" required helper="Typical production batch size needed." options={CMO_OPTIONS.batchSize} value={data.batchSize} onChange={set('batchSize')} />
                <SelectField name="annualDemand" label="Estimated annual demand" required helper="Projected total yearly volume requirement." options={CMO_OPTIONS.annualDemand} value={data.annualDemand} onChange={set('annualDemand')} />
                <SelectField name="orderFrequency" label="Order frequency" required helper="How often do you anticipate placing orders?" options={CMO_OPTIONS.orderFrequency} value={data.orderFrequency} onChange={set('orderFrequency')} />
                <SelectField name="firstDelivery" label="First delivery needed by" required helper="When do you need the first commercial shipment?" options={CMO_OPTIONS.firstDelivery} value={data.firstDelivery} onChange={set('firstDelivery')} />
              </Section>

              <Section title="Quality & Compliance">
                <MultiSelectField name="qualityStandards" label="Quality / regulatory requirements" required helper="Select all applicable quality and regulatory standards." options={CMO_OPTIONS.qualityStandards} values={data.qualityStandards} onChange={set('qualityStandards')} columns={2} />
                <ConditionalText show={(data.qualityStandards || []).includes('Other')} name="qualityStandardsOther" helper="Please describe the other quality/regulatory standard." placeholder="e.g., Industry-specific certification" value={data.qualityStandardsOther} onChange={set('qualityStandardsOther')} />
                <MultiSelectField name="supplyChain" label="Supply chain requirements" helper="Select any specific supply chain needs." options={CMO_OPTIONS.supplyChain} values={data.supplyChain} onChange={set('supplyChain')} columns={2} />
                <SelectField name="shippingDestination" label="Shipping destination" required helper="Primary delivery region for logistics planning." options={CMO_OPTIONS.shippingDestination} value={data.shippingDestination} onChange={set('shippingDestination')} />
              </Section>

              <Section title="Commercial Terms & Additional Details">
                <SelectField name="pricingModel" label="Preferred pricing model" helper="How do you prefer to structure pricing?" options={CMO_OPTIONS.pricingModel} value={data.pricingModel} onChange={set('pricingModel')} />
                <TextareaField name="additionalSpecs" label="Additional specifications or comments" helper="Describe any special requirements: cold chain logistics, hazmat shipping, acceptance testing protocols, or existing supplier concerns." placeholder="e.g., Current supplier has inconsistent lot-to-lot purity. Need CoA with full impurity profile. Product is moisture-sensitive - require desiccant packaging and N₂ purge..." value={data.additionalSpecs} onChange={set('additionalSpecs')} />
              </Section>

              <Section title="Document Upload">
                <FileUploadField name="files" label="Upload product specifications" helper="Upload product specifications, SDS, COA, drawings, or supply agreements (PDF, DOC, XLS - max 25 MB per file)." files={files} onChange={setFiles} />
                <SelectField name="howHeard" label="How did you hear about LAMKO?" helper="Help us understand our reach." options={HOW_HEARD_OPTIONS} value={data.howHeard} onChange={set('howHeard')} />
              </Section>

              <FormActions onReset={reset} isSubmitting={submitting} />
              <ConsentSection variant="customer" consent1={data.consentRequired} consent2={data.consentOptional} onChange1={set('consentRequired')} onChange2={set('consentOptional')} showError={showConsentError} />
              <PrivacyNote />
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

